import type { FocusEvent, KeyboardEvent, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextToolbarControls } from '@peruselab/structure-editor/components';
import { DEFAULT_TEXT_FONT_FAMILY, DEFAULT_TEXT_FONT_SIZE_PT } from '@peruselab/structure-editor/rendering/textLayout';
import { Icon, type IconName } from './Icon';
import { SymbolPicker } from './SymbolPicker';

// #756 -- font choices offered for standalone text. Values are CSS font-family lists, stored as
// authored; export fidelity across machines is #610/#757's concern.
export const TEXT_FONT_FAMILIES: readonly { readonly value: string; readonly label: string }[] = [
  { value: DEFAULT_TEXT_FONT_FAMILY, label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' }
];

// Buttons must not take focus from the text: the selection they act on lives there.
const keepFocus = (event: SyntheticEvent) => event.preventDefault();

// #756 -- the floating formatting bar above a text annotation being edited. Icon-only toggles,
// each with aria-label + title (docs/icon-language.md).
export function TextFormatToolbar({ controls }: { readonly controls: TextToolbarControls }) {
  const { t } = useTranslation();
  const { format } = controls;
  const size = format.fontSize === 'mixed' ? '' : String(format.fontSize ?? DEFAULT_TEXT_FONT_SIZE_PT);
  const family = format.fontFamily === 'mixed' ? '' : (format.fontFamily ?? DEFAULT_TEXT_FONT_FAMILY);

  const toggle = (icon: IconName, labelKey: string, pressed: boolean, onClick: () => void) => (
    <button
      type="button"
      className={`icon-button ${pressed ? 'active' : ''}`}
      aria-label={t(labelKey)}
      title={t(labelKey)}
      aria-pressed={pressed}
      onPointerDown={keepFocus}
      onMouseDown={keepFocus}
      onClick={onClick}
    >
      <Icon name={icon} size="1.15rem" />
    </button>
  );

  // The field is uncontrolled and keyed by the current size, so a new selection resets it.
  const applySize = (field: HTMLInputElement) => {
    const points = Number(field.value);
    if (field.value !== size && Number.isFinite(points) && points >= 4 && points <= 400) controls.setFontSize(points);
    else field.value = size;
  };

  return (
    <div className="text-format-toolbar" role="toolbar" aria-label={t('textFormat.toolbar')}>
      <select
        aria-label={t('textFormat.fontFamily')}
        title={t('textFormat.fontFamily')}
        value={family}
        onChange={(event) => controls.setFontFamily(event.target.value)}
      >
        {family === '' ? <option value="">{t('textFormat.mixed')}</option> : null}
        {TEXT_FONT_FAMILIES.some((option) => option.value === family) || family === '' ? null : (
          <option value={family}>{family}</option>
        )}
        {TEXT_FONT_FAMILIES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <input
        type="number"
        min={4}
        max={400}
        step={1}
        inputMode="numeric"
        aria-label={t('textFormat.fontSize')}
        title={t('textFormat.fontSize')}
        placeholder={t('textFormat.mixed')}
        key={size}
        defaultValue={size}
        onBlur={(event: FocusEvent<HTMLInputElement>) => applySize(event.currentTarget)}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            applySize(event.currentTarget);
          }
        }}
      />
      <span className="text-format-toolbar-divider" aria-hidden="true" />
      {toggle('formatBold', 'textFormat.bold', format.bold, controls.toggleBold)}
      {toggle('formatItalic', 'textFormat.italic', format.italic, controls.toggleItalic)}
      {toggle('subscript', 'textFormat.subscript', format.script === 'sub', () => controls.toggleScript('sub'))}
      {toggle('superscript', 'textFormat.superscript', format.script === 'super', () => controls.toggleScript('super'))}
      <span className="text-format-toolbar-divider" aria-hidden="true" />
      {toggle('formatAlignLeft', 'textFormat.alignLeft', format.align === 'left', () => controls.setAlign('left'))}
      {toggle('formatAlignCenter', 'textFormat.alignCenter', format.align === 'center', () =>
        controls.setAlign('center')
      )}
      {toggle('formatAlignRight', 'textFormat.alignRight', format.align === 'right', () => controls.setAlign('right'))}
      <span className="text-format-toolbar-divider" aria-hidden="true" />
      <SymbolPicker onInsert={controls.insertText} />
    </div>
  );
}
